<template>
  <div v-if="hasAccess" class="space-y-6 pb-10">
    <page-content>
      <section class="space-y-6">
        <div class="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <dashboard-date-filters
            class="xl:flex-1"
            :quick-ranges="quickRanges"
            :active-range-id="activeQuickRangeId"
            :date-range="dateRange"
            :max-date="maxDate"
            @select-range="applyQuickRange"
            @show-full="showFull"
            @update:date-range="handleDateRangeUpdate"
          />

          <div class="tracking-filters inline-flex flex-wrap items-center gap-3 rounded-2xl border border-gray-200 bg-white px-4 py-3 shadow-sm xl:min-w-[360px]">
            <div class="tracking-filter-field tracking-filter-field--wide">
              <t-rich-select
                :key="domainSelectKey"
                class="tracking-filter-control"
                multiple
                :close-on-select="false"
                :options="domainOptions"
                value-attribute="uuid"
                text-attribute="label"
                placeholder="Tous les domaines"
                v-model="selectedDomainUuids"
              >
                <template slot="label" slot-scope="{ options, className }">
                  <span :class="className">
                    {{ domainSelectionLabel }}
                  </span>
                </template>
              </t-rich-select>
            </div>
          </div>
        </div>

        <div class="employee-overview-grid">
          <section class="employee-space bg-white rounded-2xl shadow-sm border border-blue-200">
            <header class="employee-space-header">
              <div class="tracking-title-row">
                <h2 class="text-lg font-semibold text-gray-900">Web vs application</h2>
                <button
                  type="button"
                  class="tracking-info-button"
                  title="Suivi mois par mois"
                  aria-label="Ouvrir le suivi mois par mois"
                  @click="openMonthlyTracking"
                >
                  +
                </button>
              </div>
              <div class="tracking-period-stats">
                <article class="tracking-period-stat">
                  <span>Web</span>
                  <strong>{{ formatNumber(totals.web) }}</strong>
                </article>
                <article class="tracking-period-stat">
                  <span>Application</span>
                  <strong>{{ formatNumber(totals.native) }}</strong>
                </article>
              </div>
            </header>

            <div class="employee-card-body">
              <div class="employee-chart-area">
                <div class="tracking-chart-panel">
                  <chart-connection-trend
                    v-if="hasComparisonSeries"
                    class="h-full w-full"
                    :chart-data="comparisonChartData"
                  />
                  <div v-else class="employee-empty-state">
                    Aucune connexion identifiée sur cette période.
                  </div>
                </div>
                <div class="employee-segmented-control" aria-label="Donnée du graphique web vs application">
                  <button
                    v-for="option in comparisonChartOptions"
                    :key="option.value"
                    type="button"
                    :class="{ 'is-active': comparisonChartMode === option.value }"
                    @click="comparisonChartMode = option.value"
                  >
                    {{ option.label }}
                  </button>
                </div>
              </div>

              <article class="employee-ring-card">
                <div class="employee-ring">
                  <svg viewBox="0 0 120 120" aria-hidden="true">
                    <circle class="employee-ring-bg" cx="60" cy="60" r="48" />
                    <circle
                      class="employee-ring-progress"
                      cx="60"
                      cy="60"
                      r="48"
                      :stroke-dasharray="ringCircumference"
                      :stroke-dashoffset="comparisonRingStrokeOffset"
                    />
                  </svg>
                  <span>{{ formatPercent(comparisonRingValue) }}</span>
                </div>
                <div
                  class="employee-segmented-control employee-segmented-control--compact"
                  aria-label="Plateforme de l'anneau"
                >
                  <button
                    v-for="option in comparisonRingOptions"
                    :key="option.value"
                    type="button"
                    :class="{ 'is-active': comparisonRingMode === option.value }"
                    @click="comparisonRingMode = option.value"
                  >
                    {{ option.label }}
                  </button>
                </div>
              </article>
            </div>
          </section>

          <section class="employee-space bg-white rounded-2xl shadow-sm border border-blue-200">
            <header class="employee-space-header">
              <div class="tracking-title-row">
                <h2 class="text-lg font-semibold text-gray-900">iOS vs Android</h2>
              </div>
              <div class="tracking-period-stats">
                <article class="tracking-period-stat">
                  <span>iOS</span>
                  <strong>{{ formatNumber(totals.ios) }}</strong>
                </article>
                <article class="tracking-period-stat">
                  <span>Android</span>
                  <strong>{{ formatNumber(totals.android) }}</strong>
                </article>
              </div>
            </header>

            <div class="employee-card-body">
              <div class="employee-chart-area">
                <div class="tracking-chart-panel">
                  <chart-connection-trend
                    v-if="hasNativeSeries"
                    class="h-full w-full"
                    :chart-data="nativeChartData"
                  />
                  <div v-else class="employee-empty-state">
                    Aucune connexion via l'application sur cette période.
                  </div>
                </div>
                <div class="employee-segmented-control" aria-label="Donnée du graphique application">
                  <button
                    v-for="option in nativeChartOptions"
                    :key="option.value"
                    type="button"
                    :class="{ 'is-active': nativeChartMode === option.value }"
                    @click="nativeChartMode = option.value"
                  >
                    {{ option.label }}
                  </button>
                </div>
              </div>

              <article class="employee-ring-card">
                <div class="employee-ring">
                  <svg viewBox="0 0 120 120" aria-hidden="true">
                    <circle class="employee-ring-bg" cx="60" cy="60" r="48" />
                    <circle
                      class="employee-ring-progress"
                      cx="60"
                      cy="60"
                      r="48"
                      :stroke-dasharray="ringCircumference"
                      :stroke-dashoffset="nativeRingStrokeOffset"
                    />
                  </svg>
                  <span>{{ formatPercent(nativeRingValue) }}</span>
                </div>
                <div
                  class="employee-segmented-control employee-segmented-control--compact"
                  aria-label="Système de l'application"
                >
                  <button
                    v-for="option in nativeRingOptions"
                    :key="option.value"
                    type="button"
                    :class="{ 'is-active': nativeRingMode === option.value }"
                    @click="nativeRingMode = option.value"
                  >
                    {{ option.label }}
                  </button>
                </div>
              </article>
            </div>
          </section>
        </div>

        <section class="rounded-2xl border border-gray-200 bg-white shadow-sm">
          <header class="border-b border-gray-200 px-5 py-4">
            <div class="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h2 class="text-lg font-semibold text-gray-900">Détail client</h2>
                <p class="mt-1 text-sm text-gray-600">
                  Connexions sur la période sélectionnée. {{ coverageSentence }}
                  Les connexions non renseignées viennent de versions de l'application qui n'envoient pas encore leur plateforme : elles ne comptent pas dans le % web.
                </p>
              </div>
              <p v-if="isLoading" class="text-sm text-gray-600">Chargement des données...</p>
            </div>
          </header>
          <div class="px-2 py-4">
            <m-v-p-table
              v-if="gridOptions"
              :options="gridOptions"
              :grid-data="gridData"
              status-bar-type="total"
              @gridReady="onGridReady"
            />
          </div>
        </section>

        <div
          v-if="isMonthlyTrackingOpen"
          class="tracking-modal-overlay"
          @click.self="closeMonthlyTracking"
        >
          <div class="tracking-modal-card tracking-modal-card--wide">
            <header class="tracking-modal-header">
              <div>
                <h3 class="tracking-modal-title">Suivi mois par mois</h3>
                <p class="tracking-modal-subtitle">
                  Connexions web et application sur l'année sélectionnée.
                </p>
              </div>
              <div class="tracking-modal-domain-filter">
                <label>Client</label>
                <t-rich-select
                  :key="`${domainSelectKey}::monthly`"
                  class="tracking-filter-control"
                  multiple
                  :close-on-select="false"
                  :options="domainOptions"
                  value-attribute="uuid"
                  text-attribute="label"
                  placeholder="Tous les domaines"
                  v-model="selectedDomainUuids"
                >
                  <template slot="label" slot-scope="{ options, className }">
                    <span :class="className">
                      {{ domainSelectionLabel }}
                    </span>
                  </template>
                </t-rich-select>
              </div>
              <button
                type="button"
                class="tracking-modal-close"
                @click="closeMonthlyTracking"
              >
                ×
              </button>
            </header>

            <div class="tracking-modal-table-wrapper tracking-monthly-table-wrapper">
              <table class="tracking-modal-table tracking-monthly-table">
                <thead>
                  <tr>
                    <th>
                      <select v-model.number="selectedTrackingYear" class="tracking-year-select">
                        <option v-for="year in trackingYearOptions" :key="year" :value="year">
                          {{ year }}
                        </option>
                      </select>
                    </th>
                    <th v-for="month in monthlyTrackingRows" :key="month.key" class="is-right">
                      {{ month.label }}
                    </th>
                  </tr>
                </thead>
                <tbody v-if="!isMonthlyTrackingLoading">
                  <tr v-for="row in monthlyTrackingTableRows" :key="row.key">
                    <th>{{ row.label }}</th>
                    <td v-for="month in monthlyTrackingRows" :key="`${row.key}-${month.key}`" class="is-right">
                      {{ formatMonthlyValue(month[row.key], row.format) }}
                    </td>
                  </tr>
                </tbody>
              </table>

              <div v-if="isMonthlyTrackingLoading" class="tracking-monthly-loader" aria-live="polite">
                <span class="tracking-monthly-spinner" />
              </div>
            </div>
          </div>
        </div>
      </section>
    </page-content>
  </div>
  <div v-else class="px-6 py-10">
    <p class="text-sm text-gray-600">
      Vous n'avez pas accès à cette section.
    </p>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { debounce } from 'lodash'
import { http } from '@/services/api.service'
import { format, parseISO, subDays, subMonths, subWeeks } from 'date-fns'
import fr from 'date-fns/locale/fr'
import PageContent from '@/components/PageContent.vue'
import MVPTable from '@/components/agGrid/MVPTable.vue'
import DashboardDateFilters from '@/components/dashboard/DashboardDateFilters.vue'
import ChartConnectionTrend from '@/components/charts/ChartConnectionTrend.vue'
import { DASHBOARD_QUICK_RANGES } from './state'
import { getFullTrackingDateRange } from './domainPeriod'
import { fetchTrackingDomains } from './trackingDomains'
import { formatTrackingDomainSelectionLabel, getTrackingQueryState, normalizeTrackingDomainUuids, syncTrackingQueryFromVm } from './trackingQuery'

const DATE_FORMAT = 'yyyy-MM-dd'
const MONTH_LABELS = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc']

function numericComparator (valueA, valueB) {
  return Number(valueA || 0) - Number(valueB || 0)
}

function toIsoDate (date) {
  return format(date, DATE_FORMAT)
}

function safeParseIsoDate (value) {
  if (!value || typeof value !== 'string') {
    return null
  }

  const isoDateMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (isoDateMatch) {
    const [, year, month, day] = isoDateMatch
    return new Date(Number(year), Number(month) - 1, Number(day))
  }

  try {
    const parsed = parseISO(value)
    if (parsed instanceof Date && !Number.isNaN(parsed.getTime())) {
      return parsed
    }
  } catch (error) {
    //
  }

  return null
}

function createDefaultTotals () {
  return {
    app: 0,
    known: 0,
    unknown: 0,
    web: 0,
    ios: 0,
    android: 0,
    native: 0,
    webShare: 0,
    coverage: 0,
    users: {
      app: 0,
      web: 0,
      native: 0,
      ios: 0,
      android: 0
    }
  }
}

export default {
  name: 'app-dashboard-tracking-employees-platforms',
  components: {
    PageContent,
    MVPTable,
    DashboardDateFilters,
    ChartConnectionTrend
  },
  beforeRouteEnter (to, from, next) {
    next(async vm => {
      const hasAccess = await vm.ensureAccess()
      if (!hasAccess) {
        vm.$router.replace('/app/dashboard')
      }
    })
  },
  data () {
    const today = new Date()
    const defaultEnd = toIsoDate(today)
    const defaultStart = toIsoDate(subDays(today, 29))
    const trackingState = getTrackingQueryState(this.$route.query, {
      activeQuickRangeId: 'month-1',
      dateRange: [defaultStart, defaultEnd],
      selectedDomainUuids: []
    })

    return {
      quickRanges: DASHBOARD_QUICK_RANGES.map(range => ({ ...range })),
      activeQuickRangeId: trackingState.activeQuickRangeId,
      dateRange: trackingState.dateRange,
      maxDate: defaultEnd,
      domainOptions: [],
      selectedDomainUuids: trackingState.selectedDomainUuids,
      selectedTrackingYear: today.getFullYear(),
      comparisonChartMode: 'value',
      comparisonRingMode: 'web',
      nativeChartMode: 'value',
      nativeRingMode: 'ios',
      totals: createDefaultTotals(),
      dailySeries: [],
      monthlySeries: [],
      gridOptions: null,
      gridData: [],
      gridApi: null,
      totalRowCount: 0,
      isLoading: false,
      isMonthlyTrackingLoading: false,
      isMonthlyTrackingOpen: false,
      statsRequestSequence: 0,
      monthlyRequestSequence: 0,
      activeStatsRequestKey: null,
      activeMonthlyRequestKey: null,
      debouncedLoadStats: null,
      debouncedLoadMonthlyTracking: null,
      isInitializingTracking: true
    }
  },
  computed: {
    ...mapGetters(['roles']),
    hasAccess () {
      return this.roles.includes('admin') || this.roles.includes('community-manager')
    },
    domainSelectionLabel () {
      return formatTrackingDomainSelectionLabel(this.selectedDomainUuids, this.domainOptions)
    },
    domainSelectKey () {
      return `${this.$route.path}::${this.domainOptions.length}`
    },
    comparisonChartOptions () {
      return [
        { value: 'value', label: 'Valeur' },
        { value: 'share', label: '%' }
      ]
    },
    comparisonRingOptions () {
      return [
        { value: 'web', label: 'Web' },
        { value: 'native', label: 'Appli' }
      ]
    },
    nativeChartOptions () {
      return [
        { value: 'value', label: 'Valeur' },
        { value: 'share', label: '%' }
      ]
    },
    nativeRingOptions () {
      return [
        { value: 'ios', label: 'iOS' },
        { value: 'android', label: 'Android' }
      ]
    },
    normalizedDailySeries () {
      const rows = Array.isArray(this.dailySeries) ? this.dailySeries : []
      return rows.map(row => {
        const web = Number(row?.web || 0)
        const ios = Number(row?.ios || 0)
        const android = Number(row?.android || 0)
        const native = ios + android

        return {
          date: row?.date || '',
          web,
          ios,
          android,
          native,
          webShare: this.share(web, web + native),
          nativeShare: this.share(native, web + native),
          iosShare: this.share(ios, native),
          androidShare: this.share(android, native)
        }
      })
    },
    hasComparisonSeries () {
      return this.normalizedDailySeries.some(row => row.web > 0 || row.native > 0)
    },
    hasNativeSeries () {
      return this.normalizedDailySeries.some(row => row.native > 0)
    },
    comparisonChartData () {
      return this.comparisonChartMode === 'share'
        ? this.buildLineChartData([
          { label: '% web', key: 'webShare', color: '#2563eb' },
          { label: '% application', key: 'nativeShare', color: '#0f766e' }
        ])
        : this.buildLineChartData([
          { label: 'Connexions web', key: 'web', color: '#2563eb' },
          { label: 'Connexions application', key: 'native', color: '#0f766e' }
        ])
    },
    nativeChartData () {
      return this.nativeChartMode === 'share'
        ? this.buildLineChartData([
          { label: '% iOS', key: 'iosShare', color: '#2563eb' },
          { label: '% Android', key: 'androidShare', color: '#7c3aed' }
        ])
        : this.buildLineChartData([
          { label: 'Connexions iOS', key: 'ios', color: '#2563eb' },
          { label: 'Connexions Android', key: 'android', color: '#7c3aed' }
        ])
    },
    ringCircumference () {
      return 2 * Math.PI * 48
    },
    comparisonRingValue () {
      const web = Number(this.totals.web || 0)
      const native = Number(this.totals.native || 0)

      return this.comparisonRingMode === 'native'
        ? this.share(native, web + native)
        : this.share(web, web + native)
    },
    comparisonRingStrokeOffset () {
      return this.buildRingStrokeOffset(this.comparisonRingValue)
    },
    nativeRingValue () {
      const value = this.nativeRingMode === 'android'
        ? Number(this.totals.android || 0)
        : Number(this.totals.ios || 0)

      return this.share(value, Number(this.totals.native || 0))
    },
    nativeRingStrokeOffset () {
      return this.buildRingStrokeOffset(this.nativeRingValue)
    },
    coverageSentence () {
      return `Plateforme connue pour ${this.formatPercent(this.totals.coverage)} des connexions.`
    },
    tableTotals () {
      if (this.gridData.length === 0) {
        return null
      }

      return {
        domain_label: 'Total',
        app: this.totals.app,
        web: this.totals.web,
        native: this.totals.native,
        ios: this.totals.ios,
        android: this.totals.android,
        unknown: this.totals.unknown,
        webShare: this.totals.webShare
      }
    },
    trackingYearOptions () {
      const currentYear = new Date().getFullYear()
      const years = []
      for (let year = currentYear; year >= 2020; year--) {
        years.push(year)
      }
      return years
    },
    monthlyTrackingRows () {
      const rows = Array.isArray(this.monthlySeries) ? this.monthlySeries : []
      return MONTH_LABELS.map((label, index) => {
        const monthNumber = index + 1
        const row = rows.find(item => Number(item.month) === monthNumber) || {}

        return {
          key: monthNumber,
          label,
          app: Number(row.app || 0),
          web: Number(row.web || 0),
          native: Number(row.native || 0),
          ios: Number(row.ios || 0),
          android: Number(row.android || 0),
          unknown: Number(row.unknown || 0),
          webShare: Number(row.webShare || 0),
          nativeShare: this.share(Number(row.native || 0), Number(row.web || 0) + Number(row.native || 0))
        }
      })
    },
    monthlyTrackingTableRows () {
      return [
        { key: 'app', label: 'Connexions', format: 'number' },
        { key: 'web', label: 'Web', format: 'number' },
        { key: 'native', label: 'Application', format: 'number' },
        { key: 'webShare', label: '% web', format: 'percent' },
        { key: 'nativeShare', label: '% application', format: 'percent' },
        { key: 'ios', label: 'iOS', format: 'number' },
        { key: 'android', label: 'Android', format: 'number' },
        { key: 'unknown', label: 'Non renseignées', format: 'number' }
      ]
    }
  },
  watch: {
    selectedDomainUuids: {
      handler () {
        if (this.activeQuickRangeId === 'full') {
          this.setFullDateRange()
        }
        syncTrackingQueryFromVm(this)
        if (this.isInitializingTracking) {
          return
        }
        this.scheduleStatsReload()
        if (this.isMonthlyTrackingOpen) {
          this.scheduleMonthlyTrackingReload()
        }
      },
      deep: true
    },
    selectedTrackingYear () {
      if (!this.isInitializingTracking && this.isMonthlyTrackingOpen) {
        this.scheduleMonthlyTrackingReload()
      }
    }
  },
  async created () {
    this.initializeGridOptions()
    this.debouncedLoadStats = debounce(this.loadStats, 400)
    this.debouncedLoadMonthlyTracking = debounce(this.loadMonthlyTracking, 400)

    const hasAccess = await this.ensureAccess()
    if (!hasAccess) {
      this.$router.replace('/app/dashboard')
      return
    }

    const waitForDomains = this.activeQuickRangeId === 'full'
    const domainsPromise = this.fetchDomains()
    if (waitForDomains) {
      await domainsPromise
      this.setFullDateRange()
    }
    syncTrackingQueryFromVm(this)
    this.isInitializingTracking = false
    this.loadStats()
    if (!waitForDomains) {
      await domainsPromise
    }
  },
  beforeDestroy () {
    if (this.debouncedLoadStats) {
      this.debouncedLoadStats.cancel()
    }
    if (this.debouncedLoadMonthlyTracking) {
      this.debouncedLoadMonthlyTracking.cancel()
    }
    this.statsRequestSequence += 1
    this.monthlyRequestSequence += 1
  },
  methods: {
    async ensureAccess () {
      if (!Array.isArray(this.roles) || this.roles.length === 0) {
        try {
          await this.$store.dispatch('FETCH_ROLES')
        } catch (error) {
          return false
        }
      }

      return this.hasAccess
    },
    initializeGridOptions () {
      this.gridOptions = {
        rowModelType: 'clientSide',
        suppressMultiSort: true,
        context: {
          componentParent: this
        },
        pinnedBottomRowData: [],
        getRowClass: params => params?.node?.rowPinned ? 'tracking-total-row' : '',
        defaultColDef: {
          resizable: true,
          sortable: true,
          floatingFilter: false,
          sortingOrder: ['asc', 'desc']
        },
        columnDefs: [
          {
            headerName: 'Domaine',
            field: 'domain_label',
            minWidth: 210,
            pinned: 'left',
            sortable: true
          },
          {
            headerName: 'Connexions totales',
            field: 'app',
            minWidth: 160,
            comparator: numericComparator,
            valueFormatter: params => this.formatNumber(params.value)
          },
          {
            headerName: 'Web',
            field: 'web',
            minWidth: 120,
            comparator: numericComparator,
            sort: 'desc',
            valueFormatter: params => this.formatNumber(params.value)
          },
          {
            headerName: 'Application',
            field: 'native',
            minWidth: 130,
            comparator: numericComparator,
            valueFormatter: params => this.formatNumber(params.value)
          },
          {
            headerName: 'iOS',
            field: 'ios',
            minWidth: 120,
            comparator: numericComparator,
            valueFormatter: params => this.formatNumber(params.value)
          },
          {
            headerName: 'Android',
            field: 'android',
            minWidth: 120,
            comparator: numericComparator,
            valueFormatter: params => this.formatNumber(params.value)
          },
          {
            headerName: 'Non renseignées',
            field: 'unknown',
            minWidth: 150,
            comparator: numericComparator,
            valueFormatter: params => this.formatNumber(params.value)
          },
          {
            headerName: '% web',
            field: 'webShare',
            minWidth: 120,
            comparator: numericComparator,
            valueFormatter: params => this.formatPercent(params.value)
          }
        ]
      }
    },
    async fetchDomains () {
      try {
        const domains = await fetchTrackingDomains()
        this.domainOptions = domains

        const allowedUuids = new Set(domains.map(domain => domain.uuid))
        const currentSelectedDomainUuids = normalizeTrackingDomainUuids(this.selectedDomainUuids)
        const nextSelectedDomainUuids = currentSelectedDomainUuids
          .filter(uuid => allowedUuids.has(uuid))

        if (nextSelectedDomainUuids.join('|') !== currentSelectedDomainUuids.join('|')) {
          this.selectedDomainUuids = nextSelectedDomainUuids
        }
      } catch (error) {
        this.domainOptions = []
      }
    },
    scheduleStatsReload () {
      if (this.debouncedLoadStats) {
        this.debouncedLoadStats()
        return
      }
      this.loadStats()
    },
    scheduleMonthlyTrackingReload () {
      if (this.debouncedLoadMonthlyTracking) {
        this.debouncedLoadMonthlyTracking()
        return
      }
      this.loadMonthlyTracking()
    },
    loadStats () {
      if (this.isInitializingTracking) {
        return
      }
      if (this.debouncedLoadStats) {
        this.debouncedLoadStats.cancel()
      }

      const params = this.buildRequestParams()
      const requestKey = JSON.stringify(params)
      if (this.activeStatsRequestKey === requestKey) {
        return
      }

      const requestSequence = this.statsRequestSequence + 1
      this.statsRequestSequence = requestSequence
      this.activeStatsRequestKey = requestKey
      this.isLoading = true

      http.get('/dashboard/login-platforms', { params })
        .then(({ data }) => {
          if (requestSequence !== this.statsRequestSequence) {
            return
          }

          this.totals = this.normalizeTotals(data?.totals)
          this.dailySeries = Array.isArray(data?.dailySeries) ? data.dailySeries : []
          this.gridData = Array.isArray(data?.domains) ? data.domains : []
          this.totalRowCount = this.gridData.length
          this.updatePinnedTotals()

          if (this.gridApi) {
            this.gridApi.dispatchEvent({ type: 'totalRowCountUpdated' })
          }
        })
        .catch(() => {
          if (requestSequence !== this.statsRequestSequence) {
            return
          }

          this.totals = createDefaultTotals()
          this.dailySeries = []
          this.gridData = []
          this.totalRowCount = 0
          this.updatePinnedTotals()
          this.$buefy.toast.open({
            message: 'Impossible de charger les statistiques web / application.',
            type: 'is-danger'
          })
        })
        .finally(() => {
          if (requestSequence === this.statsRequestSequence) {
            this.isLoading = false
            this.activeStatsRequestKey = null
          }
        })
    },
    loadMonthlyTracking () {
      if (this.isInitializingTracking) {
        return
      }
      if (this.debouncedLoadMonthlyTracking) {
        this.debouncedLoadMonthlyTracking.cancel()
      }

      const params = {
        ...this.buildRequestParams(),
        trackingYear: this.selectedTrackingYear,
        monthlyOnly: 1
      }
      const requestKey = JSON.stringify(params)
      if (this.activeMonthlyRequestKey === requestKey) {
        return
      }

      const requestSequence = this.monthlyRequestSequence + 1
      this.monthlyRequestSequence = requestSequence
      this.activeMonthlyRequestKey = requestKey
      this.isMonthlyTrackingLoading = true

      http.get('/dashboard/login-platforms', { params })
        .then(({ data }) => {
          if (requestSequence !== this.monthlyRequestSequence) {
            return
          }
          this.monthlySeries = Array.isArray(data?.monthlySeries) ? data.monthlySeries : []
        })
        .catch(() => {
          if (requestSequence === this.monthlyRequestSequence) {
            this.monthlySeries = []
          }
        })
        .finally(() => {
          if (requestSequence === this.monthlyRequestSequence) {
            this.isMonthlyTrackingLoading = false
            this.activeMonthlyRequestKey = null
          }
        })
    },
    normalizeTotals (totals) {
      const defaults = createDefaultTotals()
      const result = Object.keys(defaults).reduce((acc, key) => {
        if (key !== 'users') {
          acc[key] = Number(totals?.[key] || 0)
        }
        return acc
      }, {})

      result.users = Object.keys(defaults.users).reduce((acc, key) => {
        acc[key] = Number(totals?.users?.[key] || 0)
        return acc
      }, {})

      return result
    },
    buildRequestParams () {
      const params = {}

      if (Array.isArray(this.dateRange) && this.dateRange.length === 2) {
        params.dateStart = this.dateRange[0]
        params.dateEnd = this.dateRange[1]
      }

      const selectedDomainUuids = normalizeTrackingDomainUuids(this.selectedDomainUuids)
      if (selectedDomainUuids.length > 0) {
        params.domains = selectedDomainUuids
      }

      return params
    },
    buildLineChartData (configs) {
      const rows = this.normalizedDailySeries
      return {
        labels: rows.map(item => this.formatDateLabel(item.date)),
        datasets: configs.map(config => ({
          label: config.label,
          data: rows.map(item => Number(item[config.key] || 0)),
          borderColor: config.color,
          backgroundColor: this.hexToRgba(config.color, 0.12),
          borderWidth: rows.length > 31 ? 0.75 : 1,
          pointBackgroundColor: config.color,
          pointRadius: rows.length > 31 ? 0 : (rows.length > 14 ? 1 : 1.5),
          pointHoverRadius: 2,
          pointHitRadius: 8,
          fill: false
        }))
      }
    },
    share (value, total) {
      const denominator = Number(total || 0)
      if (denominator <= 0) {
        return 0
      }

      return Math.round((Number(value || 0) / denominator) * 1000) / 10
    },
    buildRingStrokeOffset (value) {
      const progress = Math.min(100, Math.max(0, Number(value || 0)))
      return this.ringCircumference * (1 - (progress / 100))
    },
    updatePinnedTotals () {
      if (!this.gridApi) {
        return
      }

      const total = this.tableTotals
      this.gridApi.setPinnedBottomRowData(total ? [total] : [])
    },
    applyQuickRange (range) {
      if (!range) return

      this.activeQuickRangeId = range.id
      const today = new Date()
      let start = null

      if (range.type === 'week') {
        start = subWeeks(today, range.amount)
      } else if (range.type === 'month') {
        start = subMonths(today, range.amount)
      } else if (range.type === 'day') {
        start = subDays(today, range.amount - 1)
      }

      if (!start) {
        start = subDays(today, 29)
      }

      this.dateRange = [toIsoDate(start), toIsoDate(today)]
      syncTrackingQueryFromVm(this)
      this.loadStats()
    },
    showFull () {
      this.activeQuickRangeId = 'full'
      this.setFullDateRange()
      syncTrackingQueryFromVm(this)
      this.loadStats()
    },
    setFullDateRange () {
      this.dateRange = getFullTrackingDateRange(this.domainOptions, this.selectedDomainUuids)
      this.maxDate = this.dateRange[1]
    },
    handleDateRangeUpdate (range) {
      if (!Array.isArray(range) || range.length !== 2) {
        this.dateRange = null
      } else {
        this.dateRange = [...range]
      }

      this.activeQuickRangeId = null
      syncTrackingQueryFromVm(this)
      this.scheduleStatsReload()
    },
    onGridReady (params) {
      this.gridApi = params.api
      this.updatePinnedTotals()
      this.gridApi.dispatchEvent({ type: 'totalRowCountUpdated' })
    },
    formatDateLabel (value) {
      const parsed = safeParseIsoDate(value)
      if (!parsed) {
        return value
      }

      return format(parsed, 'dd/MM', { locale: fr })
    },
    formatNumber (value) {
      return new Intl.NumberFormat('fr-FR').format(Number(value || 0))
    },
    formatPercent (value) {
      return `${new Intl.NumberFormat('fr-FR', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      }).format(Number(value || 0))} %`
    },
    formatMonthlyValue (value, format) {
      if (format === 'percent') {
        return this.formatPercent(value)
      }

      return this.formatNumber(value)
    },
    hexToRgba (hex, opacity) {
      const value = String(hex || '').replace('#', '')
      if (value.length !== 6) {
        return `rgba(37, 99, 235, ${opacity})`
      }

      const r = parseInt(value.slice(0, 2), 16)
      const g = parseInt(value.slice(2, 4), 16)
      const b = parseInt(value.slice(4, 6), 16)
      return `rgba(${r}, ${g}, ${b}, ${opacity})`
    },
    openMonthlyTracking () {
      this.isMonthlyTrackingOpen = true
      this.monthlySeries = []
      if (!this.isInitializingTracking) {
        this.loadMonthlyTracking()
      }
    },
    closeMonthlyTracking () {
      this.isMonthlyTrackingOpen = false
      if (this.debouncedLoadMonthlyTracking) {
        this.debouncedLoadMonthlyTracking.cancel()
      }
    }
  }
}
</script>

<style scoped>
/* Même géométrie que l'onglet « Général » (EmployeeActivityGeneral.vue). */
.employee-overview-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 1.5rem;
}

.employee-space {
  overflow: hidden;
}

.employee-space-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.25rem;
  border-bottom: 1px solid #e5e7eb;
}

.employee-card-body {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(150px, 190px);
  gap: 1rem;
  align-items: center;
  padding: 1.25rem;
}

.employee-chart-area {
  min-width: 0;
}

.tracking-chart-panel {
  height: 14rem;
  min-width: 0;
  overflow: hidden;
}

.employee-empty-state {
  display: flex;
  height: 100%;
  align-items: center;
  justify-content: center;
  color: #4b5563;
  font-size: 0.875rem;
}

.employee-ring-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 0;
  text-align: center;
}

.employee-ring {
  position: relative;
  width: 8rem;
  height: 8rem;
}

.employee-ring svg {
  width: 100%;
  height: 100%;
  transform: rotate(-90deg);
}

.employee-ring-bg,
.employee-ring-progress {
  fill: none;
  stroke-width: 12;
}

.employee-ring-bg {
  stroke: #e5e7eb;
}

.employee-ring-progress {
  stroke: #2563eb;
  stroke-linecap: round;
  transition: stroke-dashoffset 0.25s ease;
}

.employee-ring span {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #111827;
  font-size: 1.25rem;
  font-weight: 600;
}

.employee-ring-card p {
  margin: 0.5rem 0 0;
  color: #4b5563;
  font-size: 0.875rem;
}

.employee-segmented-control {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  margin-top: 0.85rem;
  border-radius: 9999px;
  border: 1px solid transparent;
  background: #f3f4f6;
  padding: 0.25rem;
}

.employee-segmented-control button {
  min-width: 4.5rem;
  border-radius: 9999px;
  color: #4b5563;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1;
  padding: 0.45rem 0.7rem;
  transition: background 0.15s ease, color 0.15s ease;
}

.employee-segmented-control button.is-active {
  background: #2563eb;
  color: #ffffff;
}

.employee-segmented-control--compact {
  justify-content: center;
}

.employee-segmented-control--compact button {
  min-width: 4rem;
}

@media (max-width: 1180px) {
  .employee-overview-grid,
  .employee-card-body {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 640px) {
  .employee-space-header,
  .tracking-modal-header {
    flex-direction: column;
  }

  .tracking-modal-domain-filter {
    width: 100%;
    margin-left: 0;
  }

  .tracking-period-stats {
    justify-content: flex-start;
    width: 100%;
  }

  .tracking-period-stat {
    flex: 1 1 8rem;
  }

  .tracking-chart-panel {
    height: 13rem;
  }
}
</style>
